import { Add, Group, Person, Tune } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography,
  debounce,
  styled,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { useGetArnoTopObjects } from '../hooks/reactQuerys/ARNOtopQueries';
import {
  useGetLegalTransactionRecordNumbers,
  useGetLegalTransactionTypes,
  useSearchLegalTransactions,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';
import { ISearchOption } from '../Interfaces/ISearchOption';
import { SearchType } from '../Interfaces/ISearchType';
import { LegalTransactionEdit } from './LegalTransactionEdit';
import { LegalTransactionNew } from './LegalTransactionNew';
import { useGetConfig } from '../hooks/data';
import { IUserGroup, selectableUserGroups } from '../Interfaces/IUserGroup';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';

type FormType = 'edit' | 'new';

const StyledDataGrid = styled(DataGrid)({
  '& .highlighted-row': {
    backgroundColor: '#e8f1e4 !important', // Helles Grün
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#c8e0c2 !important', // Dunkleres Grün beim Hover
    },
  },
  '& .normal-row': {
    cursor: 'pointer',
  },
});

export const legalTransactionPossibleSearchOptions: ISearchOption[] = [
  { SearchOptionName: 'Titel', SearchOptionKey: 'Title' },
  {
    SearchOptionName: 'Vorgangsnummer',
    SearchOptionKey: 'TransactionNumberEntry',
  },
  { SearchOptionName: 'Urkundennummer', SearchOptionKey: 'RecordNumberEntry' },
];

const initialRowsPerPage = 25;

const LoadingOverlay = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(1px)',
        zIndex: 1,
        height: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

interface IProps {
  title: string;
  description: string;
  url: string;
  enableAdd: boolean;
  showClosed?: boolean;
  minIdLegalTransactionProgress?: number;
  maxIdLegalTransactionProgress?: number;
  showRecordNumberColumn?: boolean;
}

export const LegalTransactionOverview = ({
  title,
  description,
  url,
  enableAdd,
  showClosed,
  minIdLegalTransactionProgress,
  maxIdLegalTransactionProgress,
  showRecordNumberColumn = false,
}: IProps) => {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();

  const { config, isLoadingConfig } = useGetConfig();
  //
  const { users, isLoadingUsers } = useGetUsers();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();

  const [idLegalTransaction, setIdLegalTransaction] = useState<number | null>(
    params.idLegalTransaction ? Number(params.idLegalTransaction) : null
  );

  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialRowsPerPage);
  const [searchType, setSearchType] = useState(SearchType.INCLUDES);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOptions, setSearchOptions] = useState(
    legalTransactionPossibleSearchOptions
  );
  //
  const [idUser, setIdUser] = useState<number | null>(null);
  const [idUserGroup, setIdUserGroup] = useState<number | null>(-1);
  const [selectedType, setSelectedType] = useState('group');
  //
  const [showData, setShowData] = useState<ILegalTransaction[]>([]);
  const [showRowCount, setShowRowCount] = useState(pageSize * 2);
  //
  const getSearchUri = (
    searchTerm: string,
    localPage: number,
    localPageSize: number,
    minIdLegalTransactionProgress?: number,
    maxIdLegalTransactionProgress?: number,
    isClosed?: boolean
  ) => {
    let searchQuery = '';

    const userSearch =
      selectedType === 'user'
        ? `&idUser=${idUser}`
        : idUserGroup === -1
          ? ''
          : `&idUserGroup=${idUserGroup}`;

    let minIdLegalTransactionProgressQuery = '';
    let maxIdLegalTransactionProgressQuery = '';
    let isClosedQuery = '';

    if (minIdLegalTransactionProgress) {
      minIdLegalTransactionProgressQuery = `&minIdLegalTransactionProgress=${minIdLegalTransactionProgress}`;
    }
    if (maxIdLegalTransactionProgress) {
      maxIdLegalTransactionProgressQuery = `&maxIdLegalTransactionProgress=${maxIdLegalTransactionProgress}`;
    }
    if (isClosed) {
      isClosedQuery = `&isClosed=${isClosed}`;
    }

    for (let i = 0; i < searchOptions.length; i++) {
      searchQuery += `${searchOptions[i].SearchOptionKey}:${searchTerm};`;
    }

    return searchTerm === ''
      ? `page=${localPage + 1}&rowsPerPage=${localPageSize}${userSearch}${minIdLegalTransactionProgressQuery}${maxIdLegalTransactionProgressQuery}${isClosedQuery}`
      : `search=${searchQuery}&searchtype=${searchType}&page=${localPage + 1}&rowsPerPage=${localPageSize}${userSearch}${minIdLegalTransactionProgressQuery}${maxIdLegalTransactionProgressQuery}${isClosedQuery}`;
  };
  const [searchUri, setSearchUri] = useState(
    getSearchUri(
      '',
      page,
      pageSize,
      minIdLegalTransactionProgress,
      maxIdLegalTransactionProgress,
      showClosed
    )
  );

  //const userSearch =
  //  selectedType === 'user'
  //    ? `&idUser=${idUser}`
  //   : idUserGroup === -1
  //      ? ''
  //      : `&idUserGroup=${idUserGroup}`;
  //

  const { legalTransactionRecordNumbers } =
    useGetLegalTransactionRecordNumbers();

  const {
    legalTransactions,
    isLoadingLegalTransactions,
    abortSearchLegalTransactions,
  } = useSearchLegalTransactions(searchUri);

  const hasArnoTop = (() => {
    try {
      return config ? config.App.includes('arno_top') : false;
    } catch (e) {
      console.error(e);
      return false;
    }
  })();
  const { arnoTopObjects } = useGetArnoTopObjects(hasArnoTop);

  const [showCreateNewDialog, setShowCreateNewDialog] = useState(false);

  const textFieldRef = useRef<HTMLDivElement | null>(null);

  const combinedOptions = [
    ...(users || [])
      .filter(user => !user.isNotary && !user.isBlocked)
      .map(user => ({
        type: 'Mitarbeiter (ohne Notare)',
        id: user.idUser,
        name: `${user.FirstName} ${user.LastName}`,
      })),
    ...(
      [
        { idUserGroup: -1, UserGroup: 'Alle', Email: '', PhoneNumber: null },
      ] as IUserGroup[]
    ).map(group => ({
      type: 'Gruppe',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
    ...selectableUserGroups(userGroups).map(group => ({
      type: 'Gruppe',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
  ];

  const columns: GridColDef[] = [
    { field: 'idLegalTransaction', headerName: 'Nr.', width: 80 },
    {
      field: 'Created_at',
      headerName: 'Erstellt',
      width: 100,
      valueGetter: (params: GridValueGetterParams) =>
        params.value
          ? new Date(String(params.value)).toLocaleDateString()
          : '-',
    },
    ...(users !== undefined &&
    users.filter(user => Boolean(user.isNotary)).length > 0
      ? [
          {
            field: 'Notary_idUser',
            headerName: 'Notar',
            width: 100,
            valueGetter: (params: GridValueGetterParams) => {
              const testObject = users.find(
                user => user.idUser === Number(params.value)
              );
              if (testObject) {
                return `${testObject.LastName}`;
              } else {
                return '-';
              }
            },
          },
        ]
      : []),

    { field: 'LegalTransactionType', headerName: 'Art', width: 200 },
    { field: 'LegalTransactionProgress', headerName: 'Phase', width: 150 },
    { field: 'LegalTransactionState', headerName: 'Status', width: 150 },
    { field: 'Title', headerName: 'Bezeichnung', flex: 1 },
  ];

  useEffect(() => {
    setIdLegalTransaction(
      params.idLegalTransaction ? Number(params.idLegalTransaction) : null
    );
  }, [params.idLegalTransaction]);

  useEffect(() => {
    if (legalTransactions) {
      console.log('legalTransactions');
      console.log(legalTransactions);
      setShowData(legalTransactions);

      if (legalTransactions.length < pageSize) {
        setShowRowCount(page * pageSize + legalTransactions.length);
      } else if (legalTransactions.length > pageSize) {
        setShowRowCount(legalTransactions.length);
      } else {
        setShowRowCount((page + 1) * pageSize + pageSize);
      }

      //
      //if (legalTransactions.length < pageSize) {
      //  setShowRowCount(legalTransactions.length);
      //}
    }
  }, [legalTransactions, page, pageSize]);

  const handleClose = async (isEdited: boolean, type: FormType) => {
    if (!isEdited) {
      if (type === 'edit') {
        navigate(`/${url}`);
      } else {
        setShowCreateNewDialog(false);
      }

      return;
    }

    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Es gibt unbearbeitete Änderungen in diesem Vorgang. Sind Sie sicher, dass Sie den Vorgang ohne Speichern schließen wollen?',
      'Schließen ohne Speichern',
      'Abbrechen',
      theme
    );

    if (isConfirm) {
      if (type === 'edit') {
        navigate(`/${url}`);
      } else {
        setShowCreateNewDialog(false);
      }
    }
  };

  const handleLegalTransactionFinished = (idLegalTransaction: number) => {
    navigate(`/${url}/${idLegalTransaction}`);
    setIdLegalTransaction(idLegalTransaction);
    setShowCreateNewDialog(false);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string, localPage: number, localPageSize: number) => {
        setSearchUri(
          getSearchUri(
            term,
            localPage,
            localPageSize,
            minIdLegalTransactionProgress,
            maxIdLegalTransactionProgress,
            showClosed
          )
        );
      }, 700),
    []
  );

  const handleSearchTermChange = (term: string) => {
    //setSearchTerm(searchTerm);
    debouncedSearch(term, page, pageSize);
  };

  const newColumn: GridColDef = {
    field: 'RecordNumbers',
    headerName: 'Urkundennummer',
    width: 200,
    valueGetter: ({ row }: GridValueGetterParams) =>
      legalTransactionRecordNumbers === undefined
        ? ''
        : legalTransactionRecordNumbers
            .filter(
              x => x.idLegalTransaction === Number(row.idLegalTransaction)
            )
            .map(x => x.RecordNumberEntry)
            .join(', '),
  };

  useEffect(() => {
    abortSearchLegalTransactions();
    setSearchUri(
      getSearchUri(
        searchTerm,
        page,
        pageSize,
        minIdLegalTransactionProgress,
        maxIdLegalTransactionProgress,
        showClosed
      )
    );
  }, [idUser, idUserGroup, searchOptions]);

  const newColumns: GridColDef[] = columns
    .map((col, index) => {
      if (col.field === 'Title' && showRecordNumberColumn) {
        return [newColumn, col];
      }
      return col;
    })
    .flat();

  if (
    isLoadingConfig ||
    isLoadingUsers ||
    isLoadingUserGroups ||
    isLoadingLegalTransactionTypes
  ) {
    return <LoadingSpinner />;
  }

  // Nur echte Voraussetzungen als Fehler behandeln. NICHT `!legalTransactions` —
  // das ist beim Laden/Tab-Wechsel (Auftragsbuch ↔ Vorgänge) kurz undefined und
  // wurde früher fälschlich als Fehler angezeigt. Die Tabelle unten rendert mit
  // dem Lade-Overlay (isLoadingLegalTransactions) sauber weiter, wie gehabt.
  if (!config || !users || !userGroups || !legalTransactionTypes) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography>
          Ein Fehler ist aufgetreten! Bitte versuchen Sie es später erneut.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ m: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='h4'>{title}</Typography>

          {enableAdd && (
            <IconButton onClick={() => setShowCreateNewDialog(true)}>
              <Add />
            </IconButton>
          )}
          {!enableAdd && (
            <Alert severity='info'>
              Die Erstellung eines Vorgangs erfolgt nun im Auftragsbuch.
            </Alert>
          )}
        </Box>
        <Typography variant='caption'>{description}</Typography>

        <Grid container mt={1} spacing={1}>
          <Grid item sm={3} lg={2}>
            <Autocomplete
              sx={{ mt: 2, mb: 2 }}
              options={combinedOptions}
              groupBy={option => option.type}
              getOptionLabel={option => option.name}
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    selectedType === 'user' ? (
                      <span>
                        <Person
                          style={{
                            verticalAlign: 'middle',
                            marginRight: 8,
                          }}
                        />
                        Zugewiesener Mitarbeiter
                      </span>
                    ) : (
                      <span>
                        <Group
                          style={{
                            verticalAlign: 'middle',
                            marginRight: 8,
                          }}
                        />
                        Zugewiesene Gruppe
                      </span>
                    )
                  }
                  variant='outlined'
                />
              )}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.id}>
                  <ListItemText primary={option.name} />
                </ListItem>
              )}
              onChange={(_, newValue) => {
                setPage(0);
                if (newValue) {
                  if (newValue.type === 'Mitarbeiter (ohne Notare)') {
                    abortSearchLegalTransactions();
                    setIdUser(newValue.id);
                    setIdUserGroup(null);
                    setSelectedType('user');
                  } else if (newValue.type === 'Gruppe') {
                    abortSearchLegalTransactions();
                    setIdUser(null);
                    setIdUserGroup(newValue.id);
                    setSelectedType('group');
                  }
                } else {
                  abortSearchLegalTransactions();
                  setIdUser(null);
                  setIdUserGroup(-1);
                  setSelectedType('group');
                }
              }}
              value={
                selectedType === 'user' && idUser !== null
                  ? combinedOptions.find(
                      option =>
                        option.type === 'Mitarbeiter (ohne Notare)' &&
                        option.id === idUser
                    )
                  : selectedType === 'group' && idUserGroup !== null
                    ? combinedOptions.find(
                        option =>
                          option.type === 'Gruppe' && option.id === idUserGroup
                      )
                    : null
              }
            />
          </Grid>
          <Grid item sm={3} lg={2}>
            <TextField
              sx={{ mt: 2, mb: 2 }}
              label='Suchbedingung'
              value={searchType}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                abortSearchLegalTransactions();
                setSearchType(event.target.value as SearchType);
                setPage(0);
              }}
              fullWidth
              select
            >
              <MenuItem value={SearchType.STARTS_WITH}>
                Beginnt mit Sucheingabe
              </MenuItem>
              <MenuItem value={SearchType.INCLUDES}>
                Enthält Sucheingabe
              </MenuItem>
              <MenuItem value={SearchType.EXACT_MATCH}>
                Identisch mit Sucheingabe
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item sm={6} lg={8}>
            <TextField
              ref={textFieldRef}
              sx={{ mt: 2, mb: 2 }}
              label='Suche'
              fullWidth
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                abortSearchLegalTransactions();
                setSearchTerm(event.target.value);
                if (event.target.value) {
                  handleSearchTermChange(event.target.value);
                } else {
                  handleSearchTermChange('');
                }
                setPage(0);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() => setShowSearchOptions(prev => !prev)}
                    >
                      <Tune />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Popover
            id={'filter'}
            open={showSearchOptions}
            anchorEl={textFieldRef.current}
            onClose={() => {
              setShowSearchOptions(false);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Autocomplete
              multiple
              options={legalTransactionPossibleSearchOptions}
              getOptionLabel={option => option.SearchOptionName}
              value={searchOptions}
              onChange={(_, newValue) => {
                setSearchOptions(newValue);
                setPage(0);
              }}
              renderTags={(value: ISearchOption[], getTagProps) =>
                value.map((option: ISearchOption, index: number) => (
                  <Chip
                    label={option.SearchOptionName}
                    {...getTagProps({ index })}
                  />
                ))
              }
              style={{ padding: 10, width: textFieldRef?.current?.clientWidth }}
              isOptionEqualToValue={(
                option: ISearchOption,
                value: ISearchOption
              ) => {
                return option.SearchOptionKey === value.SearchOptionKey;
              }}
              renderInput={params => (
                <TextField {...params} variant='outlined' label='Suche nach' />
              )}
            />
          </Popover>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={12}>
            <StyledDataGrid
              loading={isLoadingLegalTransactions}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={showData}
              columns={newColumns}
              getRowId={row => row.idLegalTransaction}
              onRowClick={params =>
                navigate(`/${url}/${params.row.idLegalTransaction}`)
              }
              autoHeight
              pagination
              paginationModel={{ page, pageSize }}
              rowCount={showRowCount}
              onPaginationModelChange={model => {
                setSearchUri(
                  getSearchUri(
                    searchTerm,
                    model.page,
                    model.pageSize,
                    minIdLegalTransactionProgress,
                    maxIdLegalTransactionProgress,
                    showClosed
                  )
                );
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              paginationMode='server'
              pageSizeOptions={[initialRowsPerPage, 50, 100]}
              slots={{
                loadingOverlay: () => <LoadingOverlay />,
              }}
              getRowClassName={params =>
                params.row.idLegalTransactionState === 1
                  ? 'highlighted-row'
                  : 'normal-row'
              }
            />
          </Grid>
          <Grid
            item
            sm={12}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          ></Grid>
        </Grid>
      </Box>

      {idLegalTransaction && (
        <LegalTransactionEdit
          key={`idLegalTransaction-${idLegalTransaction}`}
          config={config}
          idLegalTransaction={idLegalTransaction}
          handleClose={isEdited => handleClose(isEdited, 'edit')}
          users={users}
          userGroups={userGroups}
          legalTransactionTypes={legalTransactionTypes}
          arnoTopObjects={arnoTopObjects}
        />
      )}

      {showCreateNewDialog && (
        <LegalTransactionNew
          config={config}
          users={users}
          userGroups={userGroups}
          legalTransactionTypes={legalTransactionTypes}
          arnoTopObjects={arnoTopObjects}
          handleClose={isEdited => handleClose(isEdited, 'new')}
          handleFinished={handleLegalTransactionFinished}
        />
      )}
    </>
  );
};
